// ============================================================================
// Pakistan fiscal adapter (FBR — Federal Board of Revenue, Digital Invoicing).
// ----------------------------------------------------------------------------
// Same interface as the Spain adapter (see ./index.js). Pakistan sales tax is a
// straight GST on the taxable base — no recargo, no IBAE, no IRPF — so the
// per-rate math reuses lib/taxes.js with the Spanish surcharges switched off.
//
// STATUS: tax math + field rules are live. `submit` posts the FBR Digital
// Invoicing payload to the backend bridge (POST /api/fbr/submit-invoice), which
// holds the encrypted Bearer token and forwards to the FBR gateway
// (postinvoicedata / postinvoicedata_sb), returning the IRN + QR.
//
// Payload shape follows the PRAL "Technical Documentation for DI API" v1.12
// (SRO 69(I)/2025). Field ENUMERATIONS that FBR validates against controlled
// lists — saleType, uoM, sroScheduleNo/sroItemSerialNo, scenarioId — are marked
// below and must be reconciled against the client's live spec before go-live.
// ============================================================================
import { groupLinesByTaxRate, round2 } from '../taxes';
import { API_BASE } from '../api';

// Pakistan standard GST is 18%. Reduced/zero rates still come off each line's
// own tax_rate, so we don't hardcode a single rate into the math — only the
// default the editor should offer.
export const PK_STANDARD_GST = 18;

// NTN = 7 digits; STRN = 13 digits; buyer may instead give a 13-digit CNIC.
const NTN_RE  = /^\d{7}$/;
const STRN_RE = /^\d{13}$/;
const CNIC_RE = /^\d{13}$/;

export const pkAdapter = {
  countryCode: 'PK',

  meta: {
    name: 'Pakistan',
    currency: 'PKR',
    currencySymbol: 'Rs',
    locale: 'en-PK',
    taxIdLabel: 'NTN / STRN',   // issuer (profiles.pk_ntn / pk_strn)
    clientTaxIdLabel: 'NTN / CNIC', // buyer (clients.tax_id)
    taxLabel: 'GST',            // primary tax name (editor headers/labels)
  },

  // Pakistan shows none of the Spanish surcharge controls.
  taxFeatures: {
    recargoEquiv: false,
    ibae: false,
    irpf: false,
    suplidos: false,
  },

  defaultTaxRate: PK_STANDARD_GST,

  // Per-line tax-rate options: standard GST 18%, reduced 8%/5%, and zero-rated.
  taxRates: [
    { value: '18', label: '18% GST' },
    { value: '8',  label: '8% GST' },
    { value: '5',  label: '5% GST' },
    { value: '0',  label: '0% GST' },
  ],

  // GST-only totals. Reuse the per-rate aggregator with the Spanish surcharges
  // off, so `iva` here is simply the GST cuota per rate.
  computeTotals(lines, opts = {}) {
    const byRate = groupLinesByTaxRate(lines, { recargoEquiv: false, applyIbi: false });
    let subtotal = 0, gstTotal = 0;
    byRate.forEach((r) => { subtotal += round2(r.productBase ?? r.base); gstTotal += round2(r.iva); });
    subtotal = round2(subtotal);
    gstTotal = round2(gstTotal);
    return {
      byRate,
      subtotal,
      taxTotal: gstTotal,
      taxLabel: 'GST',
      extraTotals: [],
      deductions: [],
      grandTotal: round2(subtotal + gstTotal),
    };
  },

  // FBR invoices require an HS code per line in addition to the buyer id.
  requiredClientFields() {
    return ['tax_id']; // buyer NTN or CNIC
  },

  requiredLineFields() {
    return ['hs_code'];
  },

  validateTaxId(id) {
    const v = String(id || '').replace(/[\s-]/g, '');
    return NTN_RE.test(v) || STRN_RE.test(v) || CNIC_RE.test(v);
  },

  einvoice: {
    enabled: true,
    featureKey: 'fbr_invoicing',   // gated per client (see feature_management.sql)
    label: 'FBR Digital Invoicing',

    // Build the FBR Digital Invoicing request body from a document. Lines carry
    // a NET base in `total` (see lib/taxes) — GST is added per line here.
    buildPayload({ invoice, lines, profile, client }) {
      const n = (v) => Number(v || 0);
      const items = (lines || []).map((ln) => {
        const rate = n(ln.tax_rate ?? ln.taxRate);
        const base = n(ln.total);                       // net line value (excl. ST)
        const salesTax = round2(base * rate / 100);
        return {
          hsCode: ln.hs_code || '',
          productDescription: ln.description || ln.name || '',
          rate: `${rate}%`,
          uoM: ln.uom || 'Numbers, pieces, units',      // ENUM — FBR UoM list
          quantity: n(ln.quantity),
          totalValues: round2(base + salesTax),
          valueSalesExcludingST: base,
          fixedNotifiedValueOrRetailPrice: 0,
          salesTaxApplicable: salesTax,
          salesTaxWithheldAtSource: 0,
          extraTax: '',
          furtherTax: 0,
          sroScheduleNo: '',                            // ENUM — SRO schedule
          fedPayable: 0,
          discount: n(ln.discount_amount),
          saleType: 'Goods at standard rate (default)', // ENUM — FBR sale type
          sroItemSerialNo: '',                          // ENUM — SRO serial
        };
      });
      return {
        invoiceType: 'Sale Invoice',
        invoiceDate: invoice?.issue_date || invoice?.date,   // YYYY-MM-DD
        sellerNTNCNIC: profile?.pk_ntn || '',
        sellerBusinessName: profile?.company_name || '',
        sellerProvince: profile?.pk_province || '',
        sellerAddress: profile?.address || '',
        buyerNTNCNIC: client?.tax_id || '',
        buyerBusinessName: client?.name || '',
        buyerProvince: client?.province || '',
        buyerAddress: client?.address || '',
        buyerRegistrationType: client?.tax_id ? 'Registered' : 'Unregistered',
        invoiceRefNo: invoice?.invoice_number || '',
        // scenarioId is REQUIRED in sandbox (drives which test scenario), ignored
        // in production. Default to the standard-rate goods scenario.
        scenarioId: invoice?.fbr_scenario_id || 'SN001',
        items,
      };
    },

    // Submit to FBR via the backend bridge. The backend attaches the seller's
    // decrypted Bearer token, routes to sandbox/production by profile.
    // fbr_environment, persists the returned IRN + QR on the invoice, and
    // returns them. Throws with FBR's validation message on rejection.
    async submit({ invoice, lines, profile, client }) {
      const payload = this.buildPayload({ invoice, lines, profile, client });
      const res = await fetch(`${API_BASE}/api/fbr/submit-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: profile?.id, invoice_id: invoice?.id, payload }),
      });
      let data = {};
      try { data = await res.json(); } catch (_) { /* non-JSON error body */ }
      if (!res.ok || data.status === 'error') {
        throw new Error(data.detail || data.message || `FBR submission failed (HTTP ${res.status})`);
      }
      return {
        fiscalNumber: data.invoiceNumber || null,
        qr: data.qr || null,
        status: data.status || 'submitted',
        raw: data.raw || data,
      };
    },
  },
};
